import { ThemeProvider } from '@cleartrip/ct-design-components'
import * as Sentry from '@sentry/nextjs'
import type { AppContext, AppProps } from 'next/app'
import NextApp from 'next/app'
import { Inter } from 'next/font/google'
import dynamic from 'next/dynamic'
import { useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { useStore } from 'react-redux'

import { setHydratedStore, wrapper } from '@/redux/store/configureStore'
import { setMultipleExperiments, setStatsigClientValues } from '@/redux/slices/statsig'
import { getCookie } from '@/utils/general/cookie'

import GlobalDialog from '@/components/common/GlobalDialog'
import GlobalModalComponent from '@/components/common/Modal'
import GlobalSidesheetComponent from '@/components/common/Sidesheet'
import { DEFAULT_ERROR_MOCK_CODE } from '@/constants/components/common'
import Layout from '@/containers/Layout'
import GlobalToast from '@/containers/ToastContainer'
import { deviceId } from '@/utils/deviceId'
import useApmInit from '@/utils/hooks/useApmInit'
import { setSentryUser } from '@/utils/sentry'
import { flushClevertapLoginProfile } from '@/utils/customEvents/clevertapProfileHelper'
import ErrorBoundary from '@/utils/widget/errorBoundary'
import ErrorFallback from '@/utils/widget/errorBoundary/errorFallback'
import { initImageErrorTracking } from '@/analytics/sparkloom'
import { initializeStatsigClient } from '@/analytics/statsig/clientConfig'
import { useAppSelector } from '@/redux/store/hooks'
import { selectStatsigClientValues } from '@/redux/slices/statsig'

const PreFetcherComponent = dynamic(
    () => import('@/components/prefetcher/PreFetcherComponent'),
    { ssr: false }
)

// Third-party base CSS must come BEFORE global.scss so app custom overrides win in cascade
import 'react-awesome-slider/dist/styles.css'
import 'react-day-picker/lib/style.css'
import 'react-input-range/lib/css/index.css'
import '@dotlottie/react-player/dist/index.css'
import '@cleartrip/ct-platform-login-form/dist/index.css'
import '@/styles/global.scss'
import '@/lib/Dots/dots.scss'
import '@/components/SimpleInputBox/simpleInputBox.scss'
import '@/components/srp/Filters/filters.scss'
import '@/components/FlightTrackingSearch/FlightTrackingSearch.scss'

// Initialize client-side Sentry monitoring
import '../../instrumentation-client'
import { SRP_REVAMP_EXPERIMENTS, SRP_REVAMP_LEGACY_COOKIE } from '@/constants/flights/srpRevamp'
import { PE_DOM_EXPERIMENT } from '@/constants/flights/premiumEconomy'

const inter = Inter({
    subsets: ['latin'],
    weight: ['100', '200', '300', '400', '500', '600', '700', '800', '900'],
    preload: true,
    display: 'swap',
    adjustFontFallback: false,
    fallback: ['Inter', 'sans-serif']
})

const StatsigClientBootstrap = () => {
    const statsigClientValues = useAppSelector(selectStatsigClientValues)
    const store = useStore()

    // Park the live client store in a module-level reference so non-React
    // code (raven helpers, plain utilities) can read state from it without
    // going through hooks. Mirrors flights-pwa's getHydratedStore pattern.
    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setHydratedStore(store as any)
    }, [store])

    useEffect(() => {
        if (typeof window === 'undefined' || !statsigClientValues) return
        const { clientKey, stableID, bootstrapValues } = statsigClientValues

        // The cookie is the persistent source of truth for stableID. Three cases:
        //   1. No cookie yet (first visit, or off-domain previews like localhost
        //      where the express middleware's domain-scoped cookie doesn't stick):
        //      write the server-supplied stableID and use the bootstrap as-is.
        //   2. Cookie matches stableID: ideal — bootstrap was generated for this
        //      same id, so use bootstrap (no network call).
        //   3. Cookie diverges from stableID (e.g. server lost track and generated
        //      a fresh id under `next dev` fallback, or stale Redux state, or QA
        //      manually set the cookie to test a bucket): cookie wins. Initialize
        //      the SDK with the cookie's stableID and DROP the bootstrap, since
        //      its evaluations were computed for the wrong user. The SDK falls
        //      back to a network fetch for the cookie's id. The cookie itself is
        //      left untouched so subsequent SSR requests stay on the same id.
        const cookieStableId = getCookie('statsig-stableid')
        let effectiveStableId = stableID
        let effectiveBootstrap: string | null = bootstrapValues

        if (!cookieStableId) {
            const newStableId = stableID || uuidv4()
            document.cookie = `statsig-stableid=${newStableId}; max-age=${365 * 24 * 60 * 60}; path=/`
            effectiveStableId = newStableId
        } else if (stableID && cookieStableId !== stableID) {
            console.warn(
                '[Statsig] stableID mismatch — cookie:',
                cookieStableId.substring(0, 8) + '...',
                'bootstrap was for:',
                stableID.substring(0, 8) + '...',
                '— honoring cookie, dropping bootstrap (will fetch from network)'
            )
            effectiveStableId = cookieStableId
            effectiveBootstrap = null
        } else {
            effectiveStableId = cookieStableId
        }

        initializeStatsigClient({
            clientKey,
            stableID: effectiveStableId,
            bootstrapValues: effectiveBootstrap
        }).catch(error => {
            console.error('[Statsig] Error initializing client:', error)
        })
    }, [statsigClientValues])

    return null
}

const App = ({ Component, pageProps, router, ...rest }: AppProps) => {

    useEffect(() => {
        deviceId()
       
        setSentryUser()

        // Post-login reload: cookie is set now, phone was stashed pre-reload. Self-guards (no-op if nothing pending).
        flushClevertapLoginProfile()

        return initImageErrorTracking()
    }, [])

    useApmInit()

    return (
        <>
            <style dangerouslySetInnerHTML={{
                __html: `
                    * {
                        font-family: ${inter.style.fontFamily} !important;
                    }
                `
            }} />
            <ThemeProvider platform="desktop" theme="B2C">
                <Sentry.ErrorBoundary fallback={({ error: _error, resetError: _resetError }) => <ErrorFallback statusCode={DEFAULT_ERROR_MOCK_CODE} />}>
                    <ErrorBoundary fallback={<ErrorFallback statusCode={DEFAULT_ERROR_MOCK_CODE} />}>
                        <StatsigClientBootstrap />
                        <Layout>
                            <Component key={router.asPath} router={router} {...pageProps} {...rest} />
                        </Layout>
                    </ErrorBoundary>
                </Sentry.ErrorBoundary>
                <GlobalModalComponent />
                <GlobalToast />
                <GlobalSidesheetComponent />
                <GlobalDialog />
                <PreFetcherComponent />
            </ThemeProvider>
        </>
    )
}

// Server-side bootstrap: generate Statsig client init values + evaluate any
// shared experiments and put them in Redux so the client renders without
// needing to wait on a Statsig network call.
;(App as any).getInitialProps = wrapper.getInitialAppProps((store: any) => async (appContext: AppContext) => {
    const appProps = await NextApp.getInitialProps(appContext)
    const { ctx } = appContext
    const { req } = ctx as any

    if (req && typeof window === 'undefined') {
        try {
            // Lazy-require so this never gets bundled into the client chunk
            // eslint-disable-next-line @typescript-eslint/no-var-requires
            const {
                getStatsigClientBootstrapValues,
                getServerExperiment,
                getStatsigServer,
                initializeStatsig
            } = require('@/analytics/statsig/serverConfig')

            // Make sure server SDK is initialized (HMR in dev can drop it)
            let statsigServer = getStatsigServer()
            if (!statsigServer) {
                statsigServer = await initializeStatsig()
            }

            // Resolve stableID from middleware-decorated req, fallback to cookie
            let stableID: string | undefined = req.statsigStableId
            if (!stableID && req.cookies) {
                stableID = req.cookies['statsig-stableid']
            }
            if (!stableID) {
                // eslint-disable-next-line @typescript-eslint/no-var-requires
                const { v4: uuidv4 } = require('uuid')
                stableID = uuidv4()
            }

            if (stableID) {
                const bootstrapValues = getStatsigClientBootstrapValues({ stableID })
                const clientKey = process.env.NEXT_PUBLIC_STATSIG_CLIENT_KEY || ''

                store.dispatch(
                    setStatsigClientValues({
                        bootstrapValues,
                        stableID,
                        clientKey
                    })
                )

                // Evaluate any cross-page experiments on the server so the
                // client can read them from Redux without flashing the
                // fallback variant. Add experiments here as they're needed.
                const experimentsToFetch: Record<
                    string,
                    { experimentName: string; paramName: string; fallback: string }
                > = {
                    seat_reco_dom: {
                        experimentName: 'seat_reco_dom',
                        paramName: 'variant',
                        fallback: ''
                    },
                    seat_reco_int: {
                        experimentName: 'seat_reco_int',
                        paramName: 'variant',
                        fallback: ''
                    },
                    new_seat_reco_dom: {
                        experimentName: 'new_seat_reco_dom',
                        paramName: 'variants',
                        fallback: ''
                    },
                    new_seat_reco_int: {
                        experimentName: 'new_seat_reco_int',
                        paramName: 'variants',
                        fallback: ''
                    },
                    checkoutflowenabled:{
                        experimentName: 'checkoutflowenabled',
                        paramName: 'variant',
                        fallback: ''
                    },
                    explore_ct_desktop: {
                        experimentName: 'explore_ct_desktop',
                        paramName: 'variant',
                        fallback: ''
                    },
                    [PE_DOM_EXPERIMENT.experimentName]: {
                        experimentName: PE_DOM_EXPERIMENT.experimentName,
                        paramName: PE_DOM_EXPERIMENT.experimentParameter,
                        fallback: ''
                    },
                    [SRP_REVAMP_EXPERIMENTS.dom.experimentName]: {
                        experimentName: SRP_REVAMP_EXPERIMENTS.dom.experimentName,
                        paramName: SRP_REVAMP_EXPERIMENTS.dom.experimentParameter,
                        fallback: ''
                    },
                    [SRP_REVAMP_EXPERIMENTS.intl.experimentName]: {
                        experimentName: SRP_REVAMP_EXPERIMENTS.intl.experimentName,
                        paramName: SRP_REVAMP_EXPERIMENTS.intl.experimentParameter,
                        fallback: ''
                    }
                }

                const experiments: Record<string, string> = {}
                Object.entries(experimentsToFetch).forEach(([key, config]) => {
                    experiments[key] = getServerExperiment({
                        userIdentifiers: { stableID: stableID as string },
                        experimentName: config.experimentName,
                        paramName: config.paramName,
                        fallback: config.fallback
                    })
                })

                const legacySrpRevampOverride = req.cookies?.[SRP_REVAMP_LEGACY_COOKIE]
                Object.values(SRP_REVAMP_EXPERIMENTS).forEach(({ cookieName, experimentName }) => {
                    const override = req.cookies?.[cookieName] || legacySrpRevampOverride
                    if (override) {
                        experiments[experimentName] = override
                    }
                })

                store.dispatch(setMultipleExperiments(experiments))
            }
        } catch (error: any) {
            console.error('[Statsig] Error in App.getInitialProps:', error)
            console.error('[Statsig] Error stack:', error?.stack)
        }
    }

    return appProps
})

export default wrapper.withRedux(App)
